import { client } from './client'
import { acquireAccessTokenOrRedirect } from '@/auth/authSession'
import { getSelectedTenantId } from '@/lib/tenantStorage'

export interface NodeUserInputAnswer {
  questionId: string
  value: string
}

export interface SubmitNodeUserInputRequest {
  requestId: string
  answers: NodeUserInputAnswer[]
  notes?: string
}

export interface PendingUserInput {
  requestId: string
  runId: string
  nodeId: string
  nodeName: string
  context: string | null
  questionsJson: string
}

export interface WorkflowRunNodeDetail {
  nodeId: string
  runId: string
  name: string
  taskType: string
  status: string
  isLlm: boolean
  startedAt: string | null
  completedAt: string | null
  durationMs: number | null
  lastExitCode: number | null
  errorMessage: string | null
  inputsJson: string | null
  outputsJson: string | null
  logs: string
  orchestratorSessionId: string | null
}

export const workflowRunsHitlApi = {
  submitUserInput: (
    projectId: string,
    runId: string,
    nodeId: string,
    body: SubmitNodeUserInputRequest,
  ) =>
    client
      .post(`/projects/${projectId}/workflow-runs/${runId}/nodes/${nodeId}/user-input`, body)
      .then((r) => r.data),

  getNodeDetail: (projectId: string, runId: string, nodeId: string) =>
    client
      .get<WorkflowRunNodeDetail>(`/projects/${projectId}/workflow-runs/${runId}/nodes/${nodeId}`)
      .then((r) => r.data),

  getPendingUserInput: (projectId: string, runId: string) =>
    client
      .get<PendingUserInput | null>(`/projects/${projectId}/workflow-runs/${runId}/pending-user-input`)
      .then((r) => r.data),
}

export async function streamChatRunEvents(
  projectId: string,
  chatId: string,
  runId: string | undefined,
  onEvent: (event: string, data: string) => void,
  signal?: AbortSignal,
) {
  const token = await acquireAccessTokenOrRedirect()
  if (!token) return

  const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api'
  const url = new URL(
    `${baseUrl}/projects/${projectId}/chats/${chatId}/stream`,
    window.location.origin,
  )
  if (runId) url.searchParams.set('runId', runId)

  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    Authorization: `Bearer ${token}`,
  }
  const tenantId = getSelectedTenantId()
  if (tenantId) headers['X-Tenant-Id'] = tenantId

  const response = await fetch(url.toString(), { method: 'GET', headers, signal })

  if (response.status === 204) return
  if (!response.ok) throw new Error(`Chat stream request failed: ${response.status}`)

  const reader = response.body?.getReader()
  if (!reader) throw new Error('No response body')

  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    let currentEvent = ''
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim()
      } else if (line.startsWith('data: ') && currentEvent) {
        const data = line.slice(6).trim()
        onEvent(currentEvent, data)
        currentEvent = ''
      }
    }
  }
}
